<script lang="ts" setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import type { ReminderDto } from "~/api";
import { getReminderAction } from "~/YYC_Housing/HMB/utils/reminderActions";
import { useReminderStore } from "~/stores/ReminderStore";
import { formatDistanceToNow } from "~/utils/dateUtils";

const props = withDefaults(
  defineProps<{
    /**
     * Which reminder family to list. The bell shows "general" (everything except support
     * tickets); the Help & Support icon shows "support" — one reminder never appears in
     * both panels.
     */
    variant?: "general" | "support";
    /**
     * Where a clicked support reminder navigates (the Support Tickets page for whichever
     * site this is mounted on). Only used by the support variant.
     */
    supportTicketsPath?: string;
  }>(),
  {
    variant: "general",
    supportTicketsPath: "/support-tickets",
  },
);

const reminderStore = useReminderStore();
const router = useRouter();

const emit = defineEmits(["close"]);

const reminders = computed(() =>
  props.variant === "support"
    ? reminderStore.supportReminders
    : reminderStore.generalReminders,
);

const title = computed(() =>
  props.variant === "support" ? "Ticket Reminders" : "Notifications",
);

const emptyText = computed(() =>
  props.variant === "support" ? "No ticket reminders" : "No notifications",
);

const hasReminders = computed(() => reminders.value.length > 0);

/**
 * The ticket a support reminder is about. Support reminder contextJson carries `ticketGuid`
 * (see SupportReminderService.BuildPayload); missing or unparseable context — e.g. a
 * reminder created before deep-linking — just means no deep link.
 */
const getTicketGuid = (reminder: ReminderDto): string | undefined => {
  if (!reminder.contextJson) return undefined;
  try {
    const context = JSON.parse(reminder.contextJson) as Record<string, unknown>;
    const guid = context.ticketGuid;
    return typeof guid === "string" && guid.length > 0 ? guid : undefined;
  } catch {
    return undefined;
  }
};

// Clicking a reminder marks it read and navigates: support reminders go to the Support
// Tickets page, deep-linked (?ticket=<guid>) so the page opens that ticket's workflow
// viewer; general ones use the deep-link action mapping (same mapping the dashboard's
// action button uses) to open the matching drawer on the dashboard.
const handleClick = (reminder: ReminderDto) => {
  if (reminder.guid) {
    reminderStore.markAsRead(reminder.guid);
  }

  if (props.variant === "support") {
    emit("close");
    const ticketGuid = getTicketGuid(reminder);
    router.push({
      path: props.supportTicketsPath,
      query: ticketGuid ? { ticket: ticketGuid } : undefined,
    });
    return;
  }

  const action = getReminderAction(reminder);
  if (action) {
    emit("close");
    router.push(action.route);
  }
};
</script>

<template>
  <div class="notification-panel">
    <div class="notification-panel-header">
      <span class="notification-panel-title">{{ title }}</span>
      <router-link
        v-if="variant === 'support'"
        :to="supportTicketsPath"
        class="notification-panel-link"
        @click="emit('close')"
      >
        View tickets
      </router-link>
    </div>

    <div v-if="!hasReminders" class="notification-panel-empty">
      {{ emptyText }}
    </div>

    <div v-else class="notification-panel-list">
      <div
        v-for="reminder in reminders"
        :key="reminder.guid"
        :class="[
          'notification-item',
          { 'notification-item--unread': !reminder.isRead },
        ]"
        @click="handleClick(reminder)"
      >
        <div class="notification-item-content">
          <div class="notification-item-title">{{ reminder.title }}</div>
          <div class="notification-item-message">{{ reminder.message }}</div>
          <div class="notification-item-time">
            {{ formatDistanceToNow(reminder.createdAt!) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
.notification-panel {
  width: 360px;
  max-height: 400px;
  display: flex;
  flex-direction: column;

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid var(--el-border-color-lighter);
  }

  &-title {
    font-weight: 600;
    font-size: 14px;
  }

  &-link {
    font-size: 12px;
    color: var(--el-color-primary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &-empty {
    padding: 32px 16px;
    text-align: center;
    color: var(--el-text-color-secondary);
    font-size: 13px;
  }

  &-list {
    overflow-y: auto;
    max-height: 350px;
  }
}

.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid var(--el-border-color-extra-light);

  &:hover {
    background-color: var(--el-fill-color-light);
  }

  &--unread {
    background-color: var(--el-color-primary-light-9);

    &:hover {
      background-color: var(--el-color-primary-light-8);
    }
  }

  &-content {
    flex: 1;
    min-width: 0;
  }

  &-title {
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 2px;
  }

  &-message {
    font-size: 12px;
    color: var(--el-text-color-regular);
    white-space: normal;
    overflow-wrap: anywhere;
  }

  &-time {
    font-size: 11px;
    color: var(--el-text-color-secondary);
    margin-top: 4px;
  }
}
</style>
